// src/pages/Home/Home.tsx

import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAccountMode } from '../../context/AccountModeContext';
import { alpacaService, Account } from '../../services/AlpacaService';
import { DEMO_ACCOUNT } from '../../services/demoData';
import { useDemo } from '../../context/DemoContext';
import { useToast } from '../../context/ToastContext';
import styles from './Home.module.css';

const PDT_LIMIT = 3; // max day trades in 5 business days for accounts < $25k

/* ── Card data ── */
interface CardDef {
  to: string;
  icon: string;
  title: string;
  desc: string;
  badge?: number;
}

const HERO_CARDS: CardDef[] = [
  { to: '/predictions', icon: '🔮', title: 'Pre-Market Brief', desc: 'AI sentiment analysis and news' },
  { to: '/quick-trade', icon: '⚡', title: 'Quick Trade', desc: 'Buy or sell any stock / ETF instantly' },
  { to: '/positions', icon: '📡', title: 'Live Positions', desc: 'Auto-refreshing positions with close controls' },
  { to: '/pnl-monitor', icon: '📊', title: 'PnL Monitor', desc: 'Track profit and loss in real time' },
  { to: '/strategist', icon: '🧠', title: 'AI Strategist', desc: "Claude's autonomous portfolio recommendations" },
  { to: '/scalper', icon: '🎯', title: 'AI Scalper', desc: 'Single-symbol intraday + options trader' },
  { to: '/leaderboard', icon: '🏆', title: 'Leaderboard', desc: 'Strategist vs You — who trades better?' },
];

interface SectionDef {
  key: string;
  label: string;
  icon: string;
  cards: CardDef[];
}

const SECTIONS: SectionDef[] = [
  {
    key: 'analytics',
    label: 'Analytics',
    icon: '📊',
    cards: [
      {
        to: '/predictions',
        icon: '🎯',
        title: 'Prediction Accuracy',
        desc: 'Track prediction quality and calibration',
      },
      {
        to: '/potential-outcomes',
        icon: '🎲',
        title: 'Potential Outcomes',
        desc: "What history says about today's setup",
      },
      { to: '/reports', icon: '📋', title: 'Reports', desc: 'Generate and review reports' },
    ],
  },
  {
    key: 'automation',
    label: 'Automation',
    icon: '🤖',
    cards: [
      { to: '/pullback', icon: '🔄', title: 'Pullback Strategy', desc: 'Mean-reversion backtest results and equity' },
    ],
  },
  {
    key: 'reference',
    label: 'Reference',
    icon: '📋',
    cards: [
      { to: '/trades', icon: '💹', title: 'Trades', desc: 'Browse and manage your trades' },
      { to: '/earnings', icon: '📅', title: 'Earnings Calendar', desc: 'Upcoming earnings for tracked stocks' },
      { to: '/glossary', icon: '📖', title: 'Glossary', desc: 'Market terms, indicators & how our bots use them' },
    ],
  },
];

const Home: React.FC = () => {
  const { user } = useAuth();
  const { mode, isLive } = useAccountMode();
  const { addToast } = useToast();
  const { isDemoMode, exitDemo } = useDemo();
  const navigate = useNavigate();
  const [account, setAccount] = useState<Account | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const toggleSection = (key: string) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  // Fetch live account for PDT tracker (skip when not authenticated or in demo mode — use demo data)
  useEffect(() => {
    if (!user || isDemoMode) {
      setAccount(DEMO_ACCOUNT);
      return;
    }
    let cancelled = false;
    const fetchAccount = async () => {
      try {
        const data = await alpacaService.getAccount('live');
        if (!cancelled) setAccount(data);
      } catch (err: any) {
        addToast(err.message || 'Failed to load account data', 'error');
      }
    };
    fetchAccount();
    const interval = setInterval(fetchAccount, 60_000); // refresh every minute
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [user, isDemoMode]);

  // Pass-through (no per-card badge injection currently). Kept as a hook
  // point in case we add badges back later.
  const getSectionCards = (section: SectionDef): CardDef[] => section.cards;

  // Onboarding checklist — show until dismissed
  const [checklistDismissed, setChecklistDismissed] = useState(
    () => localStorage.getItem('onboarding_checklist_dismissed') === 'true',
  );
  const [stratConfig, setStratConfig] = useState<any>(null);
  useEffect(() => {
    if (user && !isDemoMode && !checklistDismissed) {
      alpacaService
        .getStrategistConfig?.()
        ?.then(setStratConfig)
        .catch(() => {});
    }
  }, [user, isDemoMode, checklistDismissed]);

  const dismissChecklist = () => {
    localStorage.setItem('onboarding_checklist_dismissed', 'true');
    setChecklistDismissed(true);
  };

  const checklistItems = [
    { label: 'Connect brokerage', done: !!account, link: '/onboarding' },
    { label: 'Choose AI personality', done: !!stratConfig?.personality, link: '/strategist' },
    { label: 'Enable strategist', done: stratConfig?.enabled === true, link: '/strategist' },
    { label: 'Set up notifications', done: false, link: '/settings' },
  ];
  const checklistDone = checklistItems.filter((c) => c.done).length;
  const showChecklist = user && !isDemoMode && !checklistDismissed && checklistDone < checklistItems.length;

  return (
    <section className={styles.home}>
      {isDemoMode && (
        <div
          style={{
            background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
            padding: '10px 20px',
            borderRadius: '8px',
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span style={{ color: '#fff', fontSize: '0.9rem' }}>
            You're viewing demo data — sign up and connect your broker for live trading
          </span>
          <button
            onClick={() => {
              exitDemo();
              navigate('/onboarding');
            }}
            style={{
              background: 'rgba(255,255,255,0.2)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.3)',
              padding: '6px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.85rem',
            }}
          >
            Exit Demo
          </button>
        </div>
      )}
      <h1 className={styles.greeting}>Welcome{user ? `, ${user.username}` : ''}!</h1>
      <p className={styles.subtitle}>What would you like to do?</p>

      {/* ── Onboarding Checklist ── */}
      {showChecklist && (
        <div className={styles.checklist}>
          <div className={styles.checklistHeader}>
            <span className={styles.checklistTitle}>
              Getting Started ({checklistDone}/{checklistItems.length})
            </span>
            <button className={styles.checklistDismiss} onClick={dismissChecklist}>
              Dismiss
            </button>
          </div>
          <div className={styles.checklistProgress}>
            <div
              className={styles.checklistBar}
              style={{ width: `${(checklistDone / checklistItems.length) * 100}%` }}
            />
          </div>
          <div className={styles.checklistItems}>
            {checklistItems.map((item, i) => (
              <button
                key={i}
                className={`${styles.checklistItem} ${item.done ? styles.checklistItemDone : ''}`}
                onClick={() => !item.done && navigate(item.link)}
              >
                <span className={styles.checklistCheck}>{item.done ? '✓' : '○'}</span>
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── PDT Day Trade Tracker ── */}
      {account &&
        !account.pattern_day_trader &&
        parseFloat(account.equity) < 25000 &&
        (() => {
          const used = account.daytrade_count;
          const remaining = Math.max(0, PDT_LIMIT - used);
          const pct = (used / PDT_LIMIT) * 100;
          const isWarning = remaining === 1;
          const isDanger = remaining === 0;
          const level = isDanger ? 'danger' : isWarning ? 'warning' : 'safe';

          // Estimate when a day trade slot frees up (next market open, rolling 5-biz-day window)
          const getNextTradeAvailable = (): string | null => {
            if (remaining > 0) return null; // still have trades left
            const now = new Date();
            const et = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
            const day = et.getDay(); // 0=Sun
            let daysUntil = 1;
            if (day === 5)
              daysUntil = 3; // Fri → Mon
            else if (day === 6) daysUntil = 2; // Sat → Mon
            const next = new Date(et);
            next.setDate(next.getDate() + daysUntil);
            return next.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
          };
          const nextAvailable = getNextTradeAvailable();

          return (
            <div className={`${styles.pdtTracker} ${styles[`pdt_${level}`]}`}>
              <div className={styles.pdtHeader}>
                <span className={styles.pdtTitle}>
                  {isDanger ? '🚫' : isWarning ? '⚠️' : '📊'} PDT Day Trade Tracker
                  <span className={styles.pdtAccount}>LIVE</span>
                </span>
                <span className={styles.pdtRemaining}>
                  <strong>{remaining}</strong> of {PDT_LIMIT} remaining
                </span>
              </div>
              <div className={styles.pdtBarBg}>
                <div className={styles.pdtBarFill} style={{ width: `${pct}%` }} />
              </div>
              <div className={styles.pdtFooter}>
                <span>
                  {used} day trade{used !== 1 ? 's' : ''} used in rolling 5-day window
                </span>
                {isDanger && (
                  <span className={styles.pdtDangerText}>
                    No day trades left — next trade will trigger PDT flag!
                    {nextAvailable && <> A slot opens ~{nextAvailable} at market open.</>}
                  </span>
                )}
                {isWarning && <span className={styles.pdtWarnText}>Last day trade available — use wisely!</span>}
              </div>
            </div>
          );
        })()}

      {/* ── Hero Cards ── */}
      <div className={styles.heroGrid} data-tour="hero-grid">
        {HERO_CARDS.map((c) => (
          <Link key={c.to} to={c.to} className={styles.heroCard} data-tour={`card-${c.to.slice(1)}`}>
            <span className={styles.heroIcon}>{c.icon}</span>
            <span className={styles.heroTitle}>{c.title}</span>
            <span className={styles.heroDesc}>{c.desc}</span>
          </Link>
        ))}
      </div>

      {/* ── Collapsible Sections ── */}
      {SECTIONS.map((section) => (
        <div key={section.key} className={styles.sectionWrapper}>
          <button className={styles.sectionHeader} onClick={() => toggleSection(section.key)}>
            <span className={styles.sectionLabel}>
              {section.icon} {section.label}
            </span>
            <span className={`${styles.chevron} ${expanded[section.key] ? styles.chevronOpen : ''}`}>▸</span>
          </button>
          {expanded[section.key] && (
            <div className={styles.sectionGrid}>
              {getSectionCards(section).map((c) => (
                <Link key={c.to} to={c.to} className={styles.card}>
                  {c.badge && c.badge > 0 && <span className={styles.badge}>+{c.badge}</span>}
                  <span className={styles.icon}>{c.icon}</span>
                  <span className={styles.cardTitle}>{c.title}</span>
                  <span className={styles.cardDesc}>{c.desc}</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      ))}
    </section>
  );
};

export default Home;
